import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Chip, IconButton, FormControl, LinearProgress, MenuItem, Pagination, Paper, Select, SelectChangeEvent, Skeleton, Snackbar, SnackbarCloseReason, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography, useMediaQuery, useTheme, alpha } from '@mui/material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { paperListagemSx } from '../../../shared/components';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { useDebounce, useListagemAltura, useListagemKeyboardNav } from '../../../shared/hooks';
import { Environment } from '../../../shared/environment';
import { VendasService, IListagemMovimento } from '../../../shared/services/api/estoque/venda/venda';
import { brand } from '../../../shared/themes';
import dayjs from 'dayjs';
import { FerramentasDaListagemEntrada } from '../../../shared/components/ferramentas-da-listagem/FerramentasDaListagemEntrada';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCartRounded';
import CloudOffIcon from '@mui/icons-material/CloudOffRounded';
import { inscreverFila } from '../../../shared/offline/fila';
import { LinhaVendaMobile } from './LinhaVendaMobile';
import { ItensDaVenda, NotaFiscalVenda } from './ResumoVendaListagem';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';

const OPCOES_POR_PAGINA = [10, 25, 50, 100];

// Cor/rótulo da tag de cancelamento/devolução feito no PDV (ver
// EstornarVendaPdv.ts, backend) — mesmo critério de cor do próprio Resumo
// de Vendas do PDV (DialogResumoVendasPDV.tsx): cancelada em vermelho,
// devolvida em laranja. A venda nunca é apagada, só marcada — continua
// aparecendo aqui normalmente, só com essa marcação.
const BADGE_STATUS: Record<'cancelada' | 'devolvida', { label: string; cor: string }> = {
  cancelada: { label: 'Cancelada', cor: '#D32F2F' },
  devolvida: { label: 'Devolvida', cor: '#F57C00' },
};

export const ListagemDeVenda: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requestIdRef = useRef(0);
  const { debounce } = useDebounce();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  // No celular esconde Operação/CPF-CNPJ (secundárias) em vez de deixar a tabela rolar
  // na horizontal — cabe tudo na tela sem scroll, só com Data/Nº/Cliente/Total.
  const smDown = useMediaQuery(theme.breakpoints.down('sm'));
  const [rows, setRows] = useState<IListagemMovimento[]>([]);
  // Vendas com os itens abertos na própria listagem (seta no início da linha).
  const [expandidas, setExpandidas] = useState<Set<string>>(new Set());
  const alternarItens = (idmov: string) => setExpandidas((atual) => {
    const nova = new Set(atual);
    if (nova.has(idmov)) nova.delete(idmov); else nova.add(idmov);
    return nova;
  });
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [openMsg, setOpenMsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState<'warning' | 'error' | 'info' | 'success'>('warning');
  // Muda sempre que a fila offline muda (venda salva/alterada/excluída
  // offline, ou enviada pela sincronização) — entra nas dependências dos
  // efeitos de busca abaixo pra listagem se atualizar sozinha, sem a venda
  // "não enviada" ficar aparecendo depois de já ter chegado ao servidor.
  const [versaoFila, setVersaoFila] = useState(0);
  useEffect(() => inscreverFila(() => setVersaoFila((v) => v + 1)), []);
  const qtdNaoEnviadas = rows.filter((row) => row.offline).length;
  const ROWS_CACHE_KEY = 'vendas';
  // Altura real de uma linha: padding vertical (py: 1.25 => 20px) + line-height do texto
  // (variant body1 do tema, 1.6 * fontSize 13px = 20.8px) + borda (1px) ≈ 42px. Estava em 48
  // (superestimado) — a diferença, multiplicada pelas ~15-20 linhas que cabem na tela, é
  // exatamente o que sobrava como espaço vazio entre a última linha e o rodapé.
  const { tabelaRef, containerHeight, rowsPerPage, setRowsPerPage } = useListagemAltura({
    cacheKey: ROWS_CACHE_KEY,
    valorInicial: Environment.LIMITE_DE_LINHASSMALL,
    rowHeight: 42,
    footerReservado: 68,
    bottomGap: 24,
  });

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>) => {
    setRowsPerPage(Number(event.target.value));
  };

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') return;
    setOpenMsg(false);
  };

  const dataInicial = useMemo(() => {
    return searchParams.get('datainicial') || dayjs().startOf('month').format('DD/MM/YYYY').toString();
  }, [searchParams]);

  const dataFinal = useMemo(() => {
    return searchParams.get('datafinal') || dayjs().format('DD/MM/YYYY').toString();
  }, [searchParams]);
  const tipo = useMemo(() => {
    return searchParams.get('tipo') || 'data';
  }, [searchParams]);

  const filter = useMemo(() => {
    return searchParams.get('filter') || '';
  }, [searchParams]);

  const idparceiro = useMemo(() => {
    return searchParams.get('parceiro') || '';
  }, [searchParams]);

  const pagina = useMemo(() => {
    return Number(searchParams.get('pagina') || '1');
  }, [searchParams]);

  function verificaFormato(data: string) {
    const regex = /^\d{2}\/\d{2}\/\d{4}$/;
    return regex.test(data);
  }

  const [busca, setBusca] = useState(true);
  const [autoComplete, setAutoComplete] = useState(true);
  const [data, setData] = useState(true);

  useEffect(() => {
    if (tipo === 'data') {
      setData(true);
      setBusca(false);
      setAutoComplete(false);
      setRows([]);
      setSearchParams({ tipo: 'data', pagina: '1' }, { replace: true });
      setTotalCount(0);
    }
    else if (tipo === 'parceiro') {
      setAutoComplete(true);
      setBusca(false);
      setData(false);
      setRows([]);
      setSearchParams({ tipo: 'parceiro', pagina: '1' }, { replace: true });
      setTotalCount(0);
    }
    else if (tipo === 'operacao') {
      // Orçamento mostra data E parceiro juntos — o parceiro aqui é um filtro adicional
      // opcional, diferente do tipo "parceiro" onde ele é obrigatório para buscar. O valor
      // 'operacao' (não 'orcamento') é o que o controller do back-end espera em tipo — só
      // com esse valor exato ele aplica o filtro de operacao=... que mandamos.
      setAutoComplete(true);
      setBusca(false);
      setData(true);
      setRows([]);
      setSearchParams({ tipo: 'operacao', pagina: '1' }, { replace: true });
      setTotalCount(0);
    } else {
      setBusca(true);
      setAutoComplete(false);
      setData(false);
      setRows([]);
      setSearchParams({ tipo: 'numero', pagina: '1' }, { replace: true });
      setTotalCount(0);
    }
  }, [tipo]);

  useEffect(() => {
    if (tipo === 'numero' && filter != '') {
      const requestId = ++requestIdRef.current;
      setIsLoading(true);
      debounce(() => {
        if (requestId !== requestIdRef.current) return;
        VendasService.getAll(pagina, dataInicial, dataFinal, filter, tipo, rowsPerPage)
          .then((result) => {
            if (requestId !== requestIdRef.current) return;
            setIsLoading(false);
            if (result instanceof Error) {
              setMsgType('error');
              setMsg(result.message);
              setOpenMsg(true);
            } else {
              setTotalCount(result.totalCount);
              setRows(result.data);
            }
          });
      });
    }
  }, [filter, pagina, rowsPerPage, versaoFila]);

  useEffect(() => {
    // O "else" só deve limpar a listagem quando o tipo ativo É "data" mas o filtro de datas
    // está inválido — nunca quando o tipo ativo é outro (numero/parceiro). Antes o "else" era
    // avaliado sempre que tipo !== 'data' também, então toda vez que rowsPerPage/pagina mudava
    // (independente do tipo selecionado), este efeito zerava a listagem por cima do resultado
    // que o efeito do tipo realmente ativo estava buscando — causando o "pisca" de duas listagens.
    if (tipo !== 'data') return;

    if (verificaFormato(dataInicial) && verificaFormato(dataFinal)) {
      const requestId = ++requestIdRef.current;
      setIsLoading(true);
      debounce(() => {
        if (requestId !== requestIdRef.current) return;
        VendasService.getAll(pagina, dataInicial, dataFinal, filter, tipo, rowsPerPage)
          .then((result) => {
            if (requestId !== requestIdRef.current) return;
            setIsLoading(false);
            if (result instanceof Error) {
              setMsgType('error');
              setMsg(result.message);
              setOpenMsg(true);
            } else {
              setTotalCount(result.totalCount);
              setRows(result.data);
            }
          });
      });
    } else {
      setRows([]);
    }
  }, [dataInicial, dataFinal, pagina, tipo, rowsPerPage, versaoFila]);

  useEffect(() => {
    // Mesmo motivo do efeito acima: só limpa a listagem quando o tipo ativo É "parceiro"
    // mas o parceiro está vazio, nunca quando outro tipo está ativo.
    if (tipo !== 'parceiro') return;

    if (idparceiro != '') {
      const requestId = ++requestIdRef.current;
      setIsLoading(true);
      debounce(() => {
        if (requestId !== requestIdRef.current) return;
        VendasService.getAll(pagina, dataInicial, dataFinal, idparceiro, tipo, rowsPerPage)
          .then((result) => {
            if (requestId !== requestIdRef.current) return;
            setIsLoading(false);
            if (result instanceof Error) {
              setMsgType('error');
              setMsg(result.message);
              setOpenMsg(true);
            } else {
              setTotalCount(result.totalCount);
              setRows(result.data);
            }
          });
      });
    } else {
      setRows([]);
    }
  }, [idparceiro, pagina, rowsPerPage, versaoFila]);

  useEffect(() => {
    // Mesmo motivo dos efeitos acima: só limpa a listagem quando o tipo ativo É "operacao"
    // mas o filtro de datas está inválido. O parceiro aqui é opcional (diferente do tipo
    // "parceiro"), então não bloqueia a busca quando vazio.
    if (tipo !== 'operacao') return;

    if (verificaFormato(dataInicial) && verificaFormato(dataFinal)) {
      const requestId = ++requestIdRef.current;
      setIsLoading(true);
      debounce(() => {
        if (requestId !== requestIdRef.current) return;
        VendasService.getAll(pagina, dataInicial, dataFinal, idparceiro, tipo, rowsPerPage, Environment.URL_IDORCAMENTO)
          .then((result) => {
            if (requestId !== requestIdRef.current) return;
            setIsLoading(false);
            if (result instanceof Error) {
              setMsgType('error');
              setMsg(result.message);
              setOpenMsg(true);
            } else {
              setTotalCount(result.totalCount);
              setRows(result.data);
            }
          });
      });
    } else {
      setRows([]);
    }
  }, [dataInicial, dataFinal, idparceiro, pagina, tipo, rowsPerPage, versaoFila]);

  useEffect(() => {
    if (pagina !== 1) {
      setSearchParams({
        parceiro: idparceiro,
        filter: filter,
        tipo: tipo,
        datainicial: dataInicial,
        datafinal: dataFinal,
        pagina: '1'
      }, { replace: true });
    }
  }, [rowsPerPage]);

  const formatarDocumento = (documento: string) => {
    if (!documento) return '-';
    const apenasNumeros = documento.replace(/\D/g, '');
    if (apenasNumeros.length === 11) {
      return apenasNumeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (apenasNumeros.length === 14) {
      return apenasNumeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return documento;
  };

  const primeiroItem = totalCount === 0 ? 0 : (pagina - 1) * rowsPerPage + 1;
  const ultimoItem = Math.min(pagina * rowsPerPage, totalCount);
  const totalPaginas = Math.max(1, Math.ceil(totalCount / rowsPerPage));

  // Sem F8/refoco de busca aqui (omitindo tiposBusca/onTrocarTipo): esta tela alterna entre 4
  // widgets de busca bem diferentes entre si (datas, autocomplete de parceiro, texto de
  // número, data+parceiro de orçamento) em vez de um único campo de texto — não há um "input
  // de busca" único pra focar. A navegação entre linhas (setas/Enter/pular de página) continua
  // funcionando normalmente.
  const { linhaRefCallback, onLinhaKeyDown } = useListagemKeyboardNav({
    rowsLength: rows.length,
    pagina,
    totalPaginas,
    onIrParaPagina: (novaPagina) => setSearchParams({
      parceiro: idparceiro, filter, tipo, datainicial: dataInicial, datafinal: dataFinal, pagina: novaPagina.toString(),
    }, { replace: true }),
    onEnter: (index) => navigate(`/venda/${rows[index].idmov}`, { state: { from: `${location.pathname}${location.search}` } }),
    rowsDep: [rows],
  });

  return (
    <>
      <LayoutBaseDePagina
        titulo='Vendas'
        subtitulo='Visualize, pesquise e gerencie todas as vendas cadastradas no sistema.'
        barraDeFerramentas={
          <FerramentasDaListagemEntrada
            mostrarInputBusca={busca}
            mostrarInputBasicSelect
            mostrarInputData={data}
            mostrarAutoComplete={autoComplete}
            dataInicial={dataInicial}
            dataFinal={dataFinal}
            textoDaBusca={filter}
            textoDoSelect={tipo}
            textoBotaoNovo='Nova Venda'
            aoClicarEmNovo={() => navigate('/venda/novo', { state: { from: `${location.pathname}${location.search}` } })}
            aoMudarDataInicial={datainicial => setSearchParams({ tipo: tipo, pagina: '1', datainicial: datainicial, datafinal: dataFinal }, { replace: true })}
            aoMudarDataFinal={datafinal => setSearchParams({ tipo: tipo, pagina: '1', datainicial: dataInicial, datafinal: datafinal }, { replace: true })}
            aoMudarTextoDeBusca={texto => setSearchParams({ filter: texto, tipo: tipo, pagina: '1', datainicial: dataInicial, datafinal: dataFinal }, { replace: true })}
            aoMudarTextoDoSelect={textoselect => setSearchParams({ filter: filter, tipo: textoselect, pagina: '1', datainicial: dataInicial, datafinal: dataFinal }, { replace: true })}
            aoMudarTextoDoParceiro={(texto, texto1) => setSearchParams({ parceiroselect: texto1, parceiro: texto, filter: filter, tipo: tipo, pagina: '1', datainicial: dataInicial, datafinal: dataFinal }, { replace: true })}
            dadosSelecao={[
              { nameBD: 'data', descricao: 'Data' },
              { nameBD: 'parceiro', descricao: 'Cliente' },
              { nameBD: 'numero', descricao: 'Número' },
              { nameBD: 'operacao', descricao: 'Orçamento' },
            ]}
          />
        }
      >
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={openMsg}
          autoHideDuration={msgType === 'error' ? null : 6000}
          onClose={handleCloseMsg}
        >
          <Alert
            onClose={handleCloseMsg}
            severity={msgType}
            variant='filled'
            sx={{ width: '100%' }}
          >
            {msg}
          </Alert>
        </Snackbar>
        <Paper
          elevation={0}
          sx={{
            ...paperListagemSx(theme),
            // Altura sempre fixa no espaço calculado (nunca 'auto' encolhendo pro tamanho do
            // conteúdo) — é isso que mantém a paginação colada na base da tela mesmo quando a
            // página atual tem poucas vendas, em vez de deixar uma área em branco entre o
            // rodapé e o limite da tela.
            height: containerHeight ? `${containerHeight}px` : 'calc(100vh - 220px)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {qtdNaoEnviadas > 0 && (
            <Alert severity="warning" icon={<CloudOffIcon fontSize="small" />} sx={{ borderRadius: 0, py: 0.25 }}>
              {qtdNaoEnviadas === 1
                ? '1 venda ainda não foi enviada ao servidor'
                : `${qtdNaoEnviadas} vendas ainda não foram enviadas ao servidor`}
              {' '}— ficam salvas neste computador e são enviadas automaticamente quando a conexão voltar. Você pode abri-las e alterar se precisar.
            </Alert>
          )}
          <TableContainer
            ref={tabelaRef}
            sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}
          >
            {/* No desktop, minWidth (não width:'100%') evita que tableLayout:'fixed'
                espreme as colunas abaixo do legível — quem rola é o TableContainer.
                No celular (smDown) as colunas secundárias já saem da tabela (ver
                abaixo), então nem precisa de minWidth/scroll: width:'100%' sozinho
                cabe as 4 colunas restantes na tela. */}
            {/* Celular: lista em cartões (ver LinhaVendaMobile) no lugar da tabela. */}
            {smDown ? (
              <Box>
                {isLoading && rows.length === 0 && Array.from({ length: 6 }).map((_, i) => (
                  <Box key={i} display="flex" alignItems="center" gap={1.25} px={2} py={1.25}>
                    <Skeleton variant="rounded" width={38} height={38} sx={{ borderRadius: '12px' }} />
                    <Box flex={1}>
                      <Skeleton width="60%" />
                      <Skeleton width="40%" />
                    </Box>
                    <Skeleton width={70} />
                  </Box>
                ))}
                {rows.map((row) => (
                  <LinhaVendaMobile
                    key={row.idmov}
                    venda={row}
                    onAbrir={() => navigate(`/venda/${row.idmov}`, { state: { from: `${location.pathname}${location.search}` } })}
                  />
                ))}
                {rows.length === 0 && !isLoading && (
                  <Box textAlign="center" py={6} px={3}>
                    <ShoppingCartIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                    <Typography fontWeight={700}>Nenhuma venda encontrada</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {filter || idparceiro ? 'Tente ajustar os filtros de busca' : 'Nenhuma venda encontrada no período'}
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
            <Table size="small" sx={{ width: '100%', minWidth: smDown ? 0 : 940, tableLayout: 'fixed' }}>
              {/* sticky — só os itens (tbody) rolam dentro do TableContainer; o cabeçalho
                  fica fixo no topo. As células já têm fundo opaco (torq.surface.page, ver
                  tema), então nada aparece "por baixo" ao rolar. */}
              <TableHead sx={{ position: 'sticky', top: 0, zIndex: 2 }}>
                <TableRow>
                  <TableCell sx={{ width: '40px', px: '4px !important' }} />
                  <TableCell sx={{ width: smDown ? '92px' : '110px' }}>Data</TableCell>
                  <TableCell sx={{ width: smDown ? '64px' : '90px' }}>Nº</TableCell>
                  <TableCell>Cliente</TableCell>
                  {!smDown && <TableCell sx={{ width: '140px' }}>Operação</TableCell>}
                  <TableCell sx={{ width: '170px' }}>Nota fiscal</TableCell>
                  {!smDown && <TableCell sx={{ width: '140px' }}>CPF/CNPJ</TableCell>}
                  <TableCell align='right' sx={{ width: smDown ? '80px' : '120px' }}>Total</TableCell>
                </TableRow>
              </TableHead>

              {isLoading && rows.length === 0 && (
                <TableBody>
                  {Array.from({ length: Math.min(rowsPerPage, 10) }).map((_, index) => (
                    <TableRow key={index} sx={{ '& td': { py: 1.25, px: 1.5, borderBottom: `1px solid ${theme.torq.border.default}` } }}>
                      <TableCell />
                      <TableCell><Skeleton variant="text" width="70%" /></TableCell>
                      <TableCell><Skeleton variant="rounded" width={50} height={22} /></TableCell>
                      <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                      {!smDown && <TableCell><Skeleton variant="text" width="65%" /></TableCell>}
                      <TableCell><Skeleton variant="rounded" width={90} height={22} /></TableCell>
                      {!smDown && <TableCell><Skeleton variant="text" width="75%" /></TableCell>}
                      <TableCell align="right"><Skeleton variant="text" width="60%" sx={{ ml: 'auto' }} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
              <TableBody>
                {rows.map((row, index) => {
                  const badge = row.status ? BADGE_STATUS[row.status] : null;
                  const aberta = expandidas.has(row.idmov);
                  return (
                  <React.Fragment key={row.idmov}>
                  <TableRow
                    ref={linhaRefCallback(index)}
                    hover
                    tabIndex={0}
                    sx={{
                      cursor: 'pointer',
                      outline: 'none',
                      opacity: badge ? 0.75 : 1,
                      '&:last-child td': { borderBottom: 0 },
                      '& td': {
                        py: smDown ? 0.75 : 1.25,
                        px: smDown ? 0.75 : 1.5,
                        fontSize: smDown ? '0.75rem' : '0.8125rem',
                        borderBottom: `1px solid ${theme.torq.border.default}`,
                      },
                      // Zebrado — linhas pares num tom sutil do menu lateral (brand.menuBase),
                      // ímpares na cor de fundo padrão da listagem.
                      '&:nth-of-type(even)': {
                        backgroundColor: alpha(brand.menuBase, theme.palette.mode === 'dark' ? 0.08 : 0.04),
                      },
                      // Venda não enviada ao servidor: fundo de alerta + barra
                      // lateral, pra não confundir com as vendas já gravadas.
                      ...(row.offline ? {
                        backgroundColor: `${alpha(row.statusOffline === 'erro' ? theme.palette.error.main : theme.palette.warning.main, 0.08)} !important`,
                        boxShadow: `inset 3px 0 0 ${row.statusOffline === 'erro' ? theme.palette.error.main : theme.palette.warning.main}`,
                      } : {}),
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.035),
                      },
                      // Indicador visual de qual linha está focada — necessário porque a
                      // navegação por teclado (setas) move o foco entre as linhas.
                      '&:focus-visible': {
                        outline: `2px solid ${theme.torq.border.focus}`,
                        outlineOffset: '-2px',
                        backgroundColor: alpha(theme.palette.primary.main, 0.05),
                      },
                    }}
                    onClick={() => navigate(`/venda/${row.idmov}`, { state: { from: `${location.pathname}${location.search}` } })}
                    onKeyDown={onLinhaKeyDown(index)}
                  >
                    <TableCell sx={{ px: '4px !important' }}>
                      {!row.offline && (
                        <Tooltip title={aberta ? 'Esconder itens' : 'Ver itens da venda'}>
                          <IconButton
                            size="small"
                            aria-label="Ver itens"
                            onClick={(e) => { e.stopPropagation(); alternarItens(row.idmov); }}
                          >
                            <KeyboardArrowDownRoundedIcon fontSize="small" sx={{ transition: 'transform 150ms ease', transform: aberta ? 'rotate(180deg)' : 'none' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.primary }}>
                        {dayjs(row.emissao).format('DD/MM/YYYY')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {row.offline ? (
                        <Tooltip title={row.statusOffline === 'erro'
                          ? `Não enviada — o servidor recusou: ${row.erroOffline || 'motivo não informado'}`
                          : 'Não enviada — salva neste computador, será enviada quando a conexão voltar'}
                        >
                          <Chip
                            icon={<CloudOffIcon sx={{ fontSize: '0.85rem !important' }} />}
                            label={smDown ? '' : 'Não enviada'}
                            size="small"
                            color={row.statusOffline === 'erro' ? 'error' : 'warning'}
                            sx={{ height: '22px', fontSize: '0.625rem', fontWeight: 700, '& .MuiChip-label': { px: 0.75 } }}
                          />
                        </Tooltip>
                      ) : (
                      <Chip
                        label={row.numero || 'N/A'}
                        size="small"
                        sx={{
                          height: '22px',
                          fontSize: '0.6875rem',
                          fontWeight: 600,
                          '& .MuiChip-label': { px: 1.25 },
                          bgcolor: theme.torq.surface.level2,
                          color: theme.torq.text.secondary,
                        }}
                      />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={600} noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.primary }}>
                        {row.nome || 'Cliente não informado'}
                      </Typography>
                      {row.offline && (
                        <Typography noWrap sx={{ fontSize: '0.6875rem', fontWeight: 600, color: row.statusOffline === 'erro' ? 'error.main' : 'warning.dark' }}>
                          {row.statusOffline === 'erro'
                            ? `Venda não enviada — erro: ${row.erroOffline || 'recusada pelo servidor'}`
                            : 'Venda não enviada ao servidor'}
                        </Typography>
                      )}
                    </TableCell>
                    {!smDown && (
                      <TableCell>
                        <Typography noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.secondary }}>
                          {row.descricao || '—'}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell>
                      <NotaFiscalVenda tipo={row.nftipo} numero={row.nfnumero} status={row.nfstatus} />
                    </TableCell>
                    {!smDown && (
                      <TableCell>
                        <Typography fontFamily="monospace" noWrap sx={{ fontSize: '0.8125rem', color: theme.torq.text.secondary }}>
                          {formatarDocumento(row.cpf || '')}
                        </Typography>
                      </TableCell>
                    )}
                    <TableCell align='right'>
                      <Typography sx={{
                        fontSize: '0.8125rem', fontWeight: 700,
                        color: badge ? theme.torq.text.secondary : 'success.main',
                        textDecoration: badge ? 'line-through' : 'none',
                      }}>
                        {row.total.toLocaleString('pt-BR', {
                          style: 'currency',
                          currency: 'BRL'
                        })}
                      </Typography>
                      {badge && (
                        <Chip
                          label={badge.label}
                          size="small"
                          sx={{
                            height: '18px', fontSize: '0.625rem', fontWeight: 700, mt: 0.25,
                            color: badge.cor, bgcolor: alpha(badge.cor, 0.12),
                            '& .MuiChip-label': { px: 0.75 },
                          }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                  {aberta && (
                    <TableRow sx={{ '& > td': { bgcolor: alpha(theme.palette.primary.main, 0.03), py: 1.25, px: 2 } }}>
                      <TableCell colSpan={smDown ? 6 : 8}>
                        <ItensDaVenda idmov={row.idmov} />
                      </TableCell>
                    </TableRow>
                  )}
                  </React.Fragment>
                  );
                })}

                {/* Linhas fantasma — sem elas, quando a página atual tem menos itens que
                    rowsPerPage (última página, ou poucos resultados), sobrava um espaço em
                    branco entre o último item real e o rodapé (o <tbody> não estica sozinho
                    pra preencher a altura do flex:1 do TableContainer). rowsPerPage já é
                    calculado pra caber exatamente no espaço disponível (ver useListagemAltura),
                    então completar até esse número faz a listagem sempre encostar no rodapé.
                    Mesmo <tbody> das linhas reais — nth-of-type precisa continuar a contagem
                    pro zebrado não "resetar" numa segunda tabela. */}
                {!isLoading && rows.length > 0 && rows.length < rowsPerPage && (
                  Array.from({ length: rowsPerPage - rows.length }).map((_, index) => (
                    <TableRow
                      key={`filler-${index}`}
                      sx={{
                        '&:last-of-type td': { borderBottom: 0 },
                        '& td': {
                          py: 1.25,
                          px: 1.5,
                          borderBottom: `1px solid ${theme.torq.border.default}`,
                        },
                        '&:nth-of-type(even)': {
                          backgroundColor: alpha(brand.menuBase, theme.palette.mode === 'dark' ? 0.08 : 0.04),
                        },
                      }}
                    >
                      <TableCell colSpan={smDown ? 6 : 8}>&nbsp;</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
              {rows.length === 0 && !isLoading && (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={smDown ? 6 : 8} align='center' sx={{ py: 3 }}>
                      <Box>
                        <ShoppingCartIcon sx={{
                          fontSize: 35,
                          color: theme.palette.mode === 'dark'
                            ? alpha(theme.palette.text.secondary, 0.4)
                            : 'text.secondary',
                          mb: 1
                        }} />
                        <Typography variant='h6' color='text.secondary' gutterBottom fontSize="0.85rem">
                          {Environment.LISTAGEM_VAZIA}
                        </Typography>
                        <Typography variant='body2' color='text.secondary' fontSize="0.7rem">
                          {filter || idparceiro ? 'Tente ajustar os filtros de busca' : 'Nenhuma venda encontrada no período'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
            )}
          </TableContainer>

          {/* Rodapé/paginação — fora da <table>, num Box comum. Por estar fora da área
              rolável (TableContainer, flex:1 acima) e ser o último filho do Paper (flex
              column com altura sempre fixa), fica sempre colado na base da tela, mesmo
              quando a página atual tem poucas vendas (sem isso, sobrava uma área em branco
              entre o rodapé e o limite da tela — o antigo <TableFooter> ficava logo depois
              da última linha, não colado na base). */}
          <Box sx={{ flexShrink: 0, py: 1.25, px: 2, borderTop: `1px solid ${theme.torq.border.default}` }}>
            {isLoading && <LinearProgress sx={{ height: '1.5px', mb: 1.25 }} />}
            <Box display='flex' alignItems='center' justifyContent='space-between' flexWrap='wrap' gap={1.5}>
              <Typography variant='caption' color='text.secondary'>
                {totalCount === 0
                  ? 'Nenhuma venda encontrada'
                  : `Mostrando de ${primeiroItem} a ${ultimoItem} de ${totalCount} venda${totalCount !== 1 ? 's' : ''}`}
              </Typography>

              {totalCount > rowsPerPage && (
                <Pagination
                  page={pagina}
                  count={totalPaginas}
                  onChange={(_, newPage) => setSearchParams({
                    parceiro: idparceiro,
                    filter: filter,
                    tipo: tipo,
                    datainicial: dataInicial,
                    datafinal: dataFinal,
                    pagina: newPage.toString()
                  }, { replace: true })}
                  size="small"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                  siblingCount={1}
                  boundaryCount={1}
                  sx={{
                    '& .MuiPaginationItem-root.Mui-selected': {
                      backgroundColor: brand.menuBase,
                      color: '#fff',
                      '&:hover': { backgroundColor: '#012D3D' },
                    },
                  }}
                />
              )}

              <FormControl size='small' sx={{ minWidth: 110 }}>
                <Select<number> value={rowsPerPage} onChange={handleChangeRowsPerPage}>
                  {(OPCOES_POR_PAGINA.includes(rowsPerPage) ? OPCOES_POR_PAGINA : [...OPCOES_POR_PAGINA, rowsPerPage].sort((a, b) => a - b)).map((opcao) => (
                    <MenuItem key={opcao} value={opcao}>{opcao} / página</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Box>
        </Paper>
      </LayoutBaseDePagina>
    </>
  );
};
